/**
 * api/client.js — Stage 2
 *
 * Data layer:
 *   Cases / comments / config / stats / events / attachments → Azure Functions API (/api/...)
 *   Legacy attachment fallback (historical SharePoint URLs)   → SharePoint via Graph
 *
 * Token providers are registered via setTokenProvider() / setSpTokenProvider():
 * the API path uses Bearer tokens (apiFetch.js); the legacy attachment fallback
 * (getAttachmentBlob / fetchSpBlob) still needs an MSAL Graph/SP token.
 */

import { apiFetch, setApiTokenProvider, getApiTokenForRequest, getImpersonationHeaders, getSubSessionToken, SUB_SESSION_EXPIRED, announceSubSessionExpired } from './apiFetch.js';
import { getTherapieMapsData } from './blobClient.js';
import {
  FIRST_BATCH,
  dashboardSinceIso,
} from '../lib/caseLoadPolicy.js';

export { setApiTokenProvider };

// ── Token providers ────────────────────────────────────────────────────────────
let _getToken   = null;
let _getSpToken = null;

export function setTokenProvider(fn)   { _getToken   = fn; }
export function setSpTokenProvider(fn) { _getSpToken = fn; }

async function getToken() {
  if (!_getToken) throw new Error('[client] Token provider not set.');
  return _getToken();
}

async function getSpToken() {
  if (!_getSpToken) return null;
  return _getSpToken();
}

// Bearer header for multipart/form-data (must not set Content-Type).
async function apiAuthHeader() {
  try {
    const token = await getApiTokenForRequest();
    // Multipart uploads bypass apiFetch, so they must carry the SAME identity
    // headers it adds: the bearer token, the impersonation headers (read-only
    // guard) and the clinic-mailbox sub-identity session — without X-Sub-Session
    // the server sees the shared mailbox with no person and fails closed
    // ("You do not have access to this case.") on every upload.
    const subToken = getSubSessionToken();
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...getImpersonationHeaders(),  // so uploads hit the read-only guard when impersonating
      ...(subToken ? { 'X-Sub-Session': subToken } : {}),
    };
  } catch {
    return {};
  }
}

// ── Legacy SharePoint attachment fallback ────────────────────────────────────
// The ONLY remaining SharePoint dependency: historical case/comment attachments
// whose contentUrl predates Blob-Storage SAS URLs are still fetched via Graph
// (getAttachmentBlob / fetchSpBlob). All other data is SQL + Blob Storage.

const GRAPH    = 'https://graph.microsoft.com/v1.0';
const SITE_ID  = 'therapieclinic.sharepoint.com,b023fc9f-2264-48e0-9119-7c74f9aeff35,e868d82e-1211-459c-b115-ba9d68ea6cae';
const AE_LIST_ID = 'a94677af-fb22-424c-b20d-330f83056089';

async function graphFetchBlob(path) {
  const token = await getToken();
  const url   = path.startsWith('http') ? path : `${GRAPH}${path}`;
  const res   = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Graph error: ${res.status} ${res.statusText}`);
  return res.blob();
}

// ── Status helpers (kept for any components that import them) ─────────────────

const STATUS_DISPLAY = {
  'New': 'New',
  'Pending': 'Pending',
  'Triage': 'Pending',   // pre-migration-017 name for Pending
  'Awaiting Client Input': 'Awaiting Client Input',
  'Closed': 'Closed',
};

export function normaliseStatus(raw) {
  if (!raw) return 'New';
  return STATUS_DISPLAY[raw] ?? raw;
}

export function isActive(raw) {
  return normaliseStatus(raw) !== 'Closed';
}

// ── Response mappers ───────────────────────────────────────────────────────────

// Maps a Functions API case object → the shape components expect.
// Sets _raw with SP-style field names so CaseList staff-sort and
// CaseDetail pick() continue to work without changes.
function mapApiCase(r) {
  return {
    id:           r.id,
    spItemId:     r.spItemId     || null,
    isTest:       r.isTest === true,
    caseRef:      r.caseRef      || `AE-${r.id}`,
    title:        r.clientName,            // CaseList search uses c.title
    status:       r.status,
    isActive:     r.isActive     ?? isActive(r.status),
    severity:     r.severity     || null,
    treatment:    r.treatment    || null,
    eventType:             r.eventType             || 'Treatment',
    treatmentSubtype:      r.treatmentSubtype      || null,
    incidentCategory:      r.incidentCategory      || null,
    incidentCategoryOther: r.incidentCategoryOther || null,
    typeSpecific:          r.typeSpecific          || null,
    // Immutable submitter account (read-only card in CaseDetail)
    incidentDescription:   r.incidentDescription   || null,
    // Client review date ('YYYY-MM-DD' | null) — recorded for Code 3–5 (mig 026)
    reviewDate:            r.reviewDate            || null,
    clinic:       r.clinic       || '',
    region:       r.region       || '',
    country:      r.country      || '',
    reportedDate: r.reportedDate || null,
    createdDate:  r.reportedDate || null,
    modifiedDate: r.modifiedDate || null,
    // Open response-SLA leg (list=1 rows only): { ownerSide, dueAt, pausedAt,
    // pausedReason, breachCount, snoozeUntil } | null. undefined = not asked for.
    sla:          r.sla === undefined ? undefined : (r.sla || null),
    // Newest human touch (events + comments minus notification noise) — list=1 rows.
    lastActivityAt: r.lastActivityAt || null,
    deletedAt:    r.deletedAt    || null,
    deletedBy:    r.deletedBy    || null,
    // { GBP: 1234.50, EUR: 320.00 } — populated only for cost-tier callers
    costs:        r.costs        || null,
    // [{ id, slug, label, colour, isSystem }] — empty array when none
    tags:         Array.isArray(r.tags) ? r.tags : [],
    // SP-style _raw so CaseList staff column and CaseDetail pick() work unchanged
    _raw: {
      Title:                        r.clientName,
      DateofIncident:               r.dateOfIncident,
      Department:                   r.treatment,
      Severity:                     r.severity,
      ServiceCarriedOut:            r.serviceCarriedOut,
      StaffMemberConcerned:         r.staffMemberConcerned,
      ClinicManagerResponsible:     r.clinicManagerResponsible,
      MedicalTeamMemberResponsible: r.medicalTeamMember,
      IncidentDescription:          r.incidentDescription,
      MedicalNotes:                 r.medicalNotes,
      ClientPhoneNumber:            r.clientPhone,
      Country:                      r.country,
      Region:                       r.region,
      Clinic:                       r.clinic,
      EventType:                    r.eventType,
      TreatmentSubtype:             r.treatmentSubtype,
      IncidentCategory:             r.incidentCategory,
      IncidentCategoryOther:        r.incidentCategoryOther,
      TypeSpecific:                 r.typeSpecific,
      ReviewDate:                   r.reviewDate || null,
    },
  };
}

// Slim dashboard row — matches GET /api/cases?summary=1 (no tags/costs/client name).
function mapSummaryApiCase(r) {
  return {
    id:           r.id,
    caseRef:      r.caseRef      || `AE-${r.id}`,
    status:       r.status,
    isActive:     r.isActive     ?? isActive(r.status),
    severity:     r.severity     || null,
    treatment:    r.treatment    || null,
    eventType:    r.eventType    || 'Treatment',
    clinic:       r.clinic       || '',
    region:       r.region       || '',
    country:      r.country      || '',
    reportedDate: r.reportedDate || null,
    createdDate:  r.reportedDate || null,
    _raw: {
      StaffMemberConcerned: r.staffMemberConcerned || null,
    },
  };
}

// Maps SQL attachment rows (from getCase API) → shape AttachmentItem expects.
// sasUrl is a 2-hour read SAS generated by the API; set as contentUrl so
// existing image display + download links work without any SP calls.
function mapAttachment(a) {
  return {
    id:         `att-${a.id}`,
    name:       a.fileName,
    size:       a.sizeBytes    || null,
    contentUrl: a.sasUrl       || null,
    blobPath:   a.blobPath,
    uploadedBy: a.uploadedBy   || null,
    uploadedAt: a.uploadedAt   || null,
    // null = case-level (always visible); true/false = inherits from parent comment
    isPublic:   a.isPublic     ?? null,
    // 'req:<id>' binds the file to a case requirement (satisfaction is derived
    // from this, not a flag). null for ordinary uploads.
    category:   a.category     || null,
    // Per-category upload metadata (attachments.metadata, migration 026):
    // { takenOn } for post photos, { trainingDate, trainerName } for training
    // certificates. Parsed object | null — kept as-is (immutable after upload).
    metadata:   parseAttachmentMetadata(a.metadata),
    _caseId:    '',
  };
}

function parseAttachmentMetadata(m) {
  if (m == null || m === '') return null;
  if (typeof m === 'object') return m;
  try { const parsed = JSON.parse(m); return parsed && typeof parsed === 'object' ? parsed : null; }
  catch { return null; }
}

// ── Cases — SQL API ────────────────────────────────────────────────────────────

// Login prefetch — warmed during Auth bootstrap so Dashboard / All Cases open
// without a second full round-trip after the warming screen dismisses.
let _loginPrefetch = null;

function casesQuery(filters = {}) {
  const params = new URLSearchParams();
  if (filters.status)  params.set('status', filters.status);
  if (filters.search)  params.set('search', filters.search);
  if (filters.since)   params.set('since', filters.since);
  if (filters.deleted) params.set('deleted', 'only');
  // SuperAdmin opt-in: cases filed against a TEST clinic, excluded everywhere by
  // default so the list agrees with the dashboard. The server ignores it for
  // everyone else, so it is never a client-side gate.
  if (filters.test)    params.set('test', '1');
  if (filters.limit)   params.set('limit',  String(filters.limit));
  if (filters.offset)  params.set('offset', String(filters.offset));
  if (filters.summary) params.set('summary', '1');
  if (filters.list)    params.set('list', '1');
  const query = params.toString();
  return `/api/cases${query ? `?${query}` : ''}`;
}

async function fetchCases(filters = {}, accessToken) {
  const data   = await apiFetch(casesQuery(filters), { accessToken });
  const mapper = filters.summary ? mapSummaryApiCase : mapApiCase;
  let   items  = (Array.isArray(data) ? data : []).map(mapper);
  if (filters.activeOnly) items = items.filter(i => i.isActive);
  return items;
}

/** Fire parallel case/stats fetches during login — await before dismissing warming screen. */
export function armLoginPrefetch(accessToken) {
  if (!accessToken) return null;
  const dashSince = dashboardSinceIso();
  _loginPrefetch = {
    stats:       apiFetch('/api/stats', { accessToken }),
    listFirst:   fetchCases({ list: true, limit: FIRST_BATCH }, accessToken),
    dashSummary: fetchCases({ summary: true, since: dashSince }, accessToken),
    _dashSince:  dashSince,
  };
  return _loginPrefetch;
}

let _dashPrefetch = null;

/** Warm dashboard summary while the user is on All Cases. */
export function prefetchDashboardSummary() {
  if (_dashPrefetch || _loginPrefetch?.dashSummary) return _dashPrefetch || _loginPrefetch.dashSummary;
  _dashPrefetch = fetchCases({ summary: true, since: dashboardSinceIso() });
  return _dashPrefetch;
}

function takeLoginPrefetch(filters) {
  // Login prefetch runs during the warming screen before impersonation is applied
  // in the UI — skip cached results whenever assume-identity headers are active.
  if (getImpersonationHeaders()['X-Impersonate-Email']) return null;
  if (!_loginPrefetch) return null;
  const dashSince = _loginPrefetch._dashSince || dashboardSinceIso();
  if (filters.summary && !filters.limit && !filters.offset && !filters.search && !filters.deleted
      && filters.since === dashSince) {
    const p = _loginPrefetch.dashSummary;
    _loginPrefetch.dashSummary = null;
    return p;
  }
  if (filters.list && filters.limit === FIRST_BATCH && !filters.offset && !filters.search
      && !filters.deleted && !filters.since) {
    const p = _loginPrefetch.listFirst;
    _loginPrefetch.listFirst = null;
    return p;
  }
  return null;
}

function takeDashboardPrefetch(filters) {
  if (!filters.summary || filters.limit || filters.search) return null;
  if (_dashPrefetch && filters.since === dashboardSinceIso()) {
    const p = _dashPrefetch;
    _dashPrefetch = null;
    return p;
  }
  return null;
}

export async function getCases(filters = {}, opts = {}) {
  if (!opts.accessToken) {
    const prefetched = takeLoginPrefetch(filters) || takeDashboardPrefetch(filters);
    if (prefetched) return prefetched;
  }
  return fetchCases(filters, opts.accessToken);
}

// ── Clinic mailbox sub-identity onboarding ───────────────────────────────────
// Is this tab's clinic PIN session still valid? By default a PEEK — it must not
// count as activity, or a terminal left open would never time out. `touch: true`
// is sent only when the person is actually interacting (typing a long form with
// no API calls) and slides the server's idle expiry like any other request.
export async function getClinicSessionStatus({ touch = false } = {}) {
  return apiFetch('/api/clinic/context?status=1', touch ? {} : { headers: { 'X-Sub-Session-Peek': '1' } });
}

// context: for a ClinicMailbox login, { isClinicMailbox, clinicName, roster };
// any other role → { isClinicMailbox: false } so the gate is a pass-through.
export async function getClinicContext() {
  return apiFetch('/api/clinic/context');
}
export async function requestClinicRoster({ fullName, jobTitle, note }) {
  return apiFetch('/api/clinic/roster/request', {
    method: 'POST',
    body: JSON.stringify({ fullName, jobTitle, note }),
  });
}
// Sub-identity PIN + session.
export async function setSubPin(subId, pin) {
  return apiFetch('/api/subsession/set-pin', { method: 'POST', body: JSON.stringify({ subId, pin }) });
}
export async function verifySubSession(subId, pin) {
  return apiFetch('/api/subsession/verify', { method: 'POST', body: JSON.stringify({ subId, pin }) });
}
export async function requestSubPinReset(subId) {
  return apiFetch('/api/subsession/reset-request', { method: 'POST', body: JSON.stringify({ subId }) });
}
// Clear a declined-notice for the whole clinic (syncs across terminals).
export async function dismissRosterDeclined(subId) {
  return apiFetch('/api/clinic/roster/dismiss', { method: 'POST', body: JSON.stringify({ subId }) });
}

// Case-level sharing with a registered clinic staff member (canEditCase).
export async function getCaseShares(caseId) {
  return apiFetch(`/api/cases/${caseId}/shares`);
}
export async function shareCaseWith(caseId, subId, days) {
  return apiFetch(`/api/cases/${caseId}/shares`, { method: 'POST', body: JSON.stringify({ subId, ...(days ? { days } : {}) }) });
}
export async function revokeCaseShare(caseId, shareId) {
  return apiFetch(`/api/cases/${caseId}/shares/${shareId}`, { method: 'DELETE' });
}

// Admin staff management for a clinic mailbox (manageClinicAccess).
export async function adminGetClinicStaff(email) {
  return apiFetch(`/api/clinic/staff?email=${encodeURIComponent(email)}`);
}
export async function adminClinicStaffAction(email, action, extra = {}) {
  return apiFetch('/api/clinic/staff', { method: 'POST', body: JSON.stringify({ email, action, ...extra }) });
}

// Supervisor approvals inbox (manageClinicAccess).
export async function getClinicRequests() {
  return apiFetch('/api/clinic/requests');
}
export async function getClinicRequestsCount() {
  return apiFetch('/api/clinic/requests/count');
}
export async function decideClinicRequest(id, decision, kind = 'name', reason) {
  return apiFetch(`/api/clinic/requests/${id}/decide`, {
    method: 'POST',
    body: JSON.stringify({ decision, kind, ...(reason ? { reason } : {}) }),
  });
}

export async function getCase(id) {
  const r = await apiFetch(`/api/cases/${id}`);
  const base = mapApiCase(r);
  return {
    ...base,
    closedAt:           r.closedAt           || null,
    attachments:        (r.attachments || []).map(mapAttachment),
    attachmentError:    null,
    hasAttachmentsFlag: (r.attachments?.length || 0) > 0,
  };
}

export async function postCase(data) {
  // Body field names match NewCase.jsx's form (Title, Clinic, DateofIncident, etc.)
  const result = await apiFetch('/api/cases', {
    method: 'POST',
    body:   JSON.stringify(data),
  });
  return { id: result.id, caseRef: result.caseRef, message: 'Case created successfully' };
}

// Normalise SP-style keys (from edit forms) → API camelCase expected by patchCase.js
const SP_TO_PATCH = {
  DateofIncident:               'dateOfIncident',
  Department:                   'department',
  ServiceCarriedOut:            'serviceCarriedOut',
  Title:                        'clientName',
  StaffMemberConcerned:         'staffMemberConcerned',
  ClinicManagerResponsible:     'clinicManagerResponsible',
  MedicalTeamMemberResponsible: 'medicalTeamMember',
  MedicalNotes:                 'medicalNotes',
  ClientPhoneNumber:            'clientPhone',
  Severity:                     'severity',
  Clinic:                       'clinic',
  Region:                       'region',
  Country:                      'country',
  Status:                       'status',
  TreatmentSubtype:             'treatmentSubtype',
  IncidentCategory:             'incidentCategory',
  IncidentCategoryOther:        'incidentCategoryOther',
  TypeSpecific:                 'typeSpecific',
  ReviewDate:                   'reviewDate',
};

export async function deleteCase(id) {
  return apiFetch(`/api/cases/${id}`, { method: 'DELETE' });
}

export async function restoreCase(id) {
  return apiFetch(`/api/cases/${id}/restore`, { method: 'POST' });
}

export async function patchCase(id, updates) {
  const body = {};
  for (const [k, v] of Object.entries(updates)) {
    body[SP_TO_PATCH[k] || k] = v;
  }
  return apiFetch(`/api/cases/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
}

// ── Tags — SQL API ──────────────────────────────────────────────────────────────
// Tags organise/filter cases. System tags (isSystem) are not user-removable.

export async function getTags() {
  const data = await apiFetch('/api/tags');
  return Array.isArray(data) ? data : [];
}

// Add a tag to a case. Pass { tagId } to link an existing tag, or
// { label, slug? } to create-if-new (a user tag) then link.
export async function addCaseTag(caseId, payload) {
  return apiFetch(`/api/cases/${caseId}/tags`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
}

export async function removeCaseTag(caseId, tagId) {
  return apiFetch(`/api/cases/${caseId}/tags/${tagId}`, { method: 'DELETE' });
}

// ── Tags — admin (SuperAdmin only) ───────────────────────────────────────────
// Admin cleanup variant of getTags: each tag carries a `useCount` (cases tagged).
// SuperAdmin-gated server-side; the UI only calls these from the Admin Panel.

export async function getTagsAdmin() {
  const data = await apiFetch('/api/tags/admin');
  return Array.isArray(data) ? data : [];
}

// Hard-delete a tag from the vocabulary + cascade its case links. System tags
// are rejected (403) server-side.
export async function deleteTag(id) {
  return apiFetch(`/api/tags/${id}`, { method: 'DELETE' });
}

// Create a tag in the vocabulary (SuperAdmin). Body { slug?, label, colour?,
// isSystem? }. Returns the created tagDto (or the existing one on slug dedup).
export async function createTag({ label, slug, colour, isSystem, visibleInDropdown } = {}) {
  const body = { label };
  if (slug   != null) body.slug = slug;
  if (colour != null) body.colour = colour;
  if (isSystem != null) body.isSystem = isSystem;
  if (visibleInDropdown != null) body.visibleInDropdown = visibleInDropdown;
  return apiFetch('/api/tags', { method: 'POST', body: JSON.stringify(body) });
}

// Update an existing tag (SuperAdmin). Only the provided keys change; the slug
// is stable. `colour` may be a palette class, a #hex, or null (clears it).
export async function updateTag(id, { label, colour, visibleInDropdown } = {}) {
  const body = {};
  if (label != null) body.label = label;
  if (colour !== undefined) body.colour = colour;
  if (visibleInDropdown != null) body.visibleInDropdown = visibleInDropdown;
  return apiFetch(`/api/tags/${id}`, { method: 'PUT', body: JSON.stringify(body) });
}

// ── Tag rules — auto-tagging (SuperAdmin only) ────────────────────────────────
// Save a tag's auto-tagging rule (recursive AND/OR group) + optional colour. The
// rule is validated against the field/operator allowlist server-side; saving a
// rule locks the tag as a system tag. Pass rule:null to clear. Returns the saved
// summary { id, slug, isSystem, saved }.
export async function saveTagRule(id, { rule, colour } = {}) {
  const body = { rule };
  if (colour !== undefined) body.colour = colour;
  return apiFetch(`/api/tags/${id}/rule`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// Re-apply all rule tags to every case (SuperAdmin). Returns
// { cases, added, removed, changedCases }.
export async function backfillTagRules() {
  return apiFetch('/api/tags/rules/backfill', { method: 'POST' });
}

// Preview how many live cases match an in-progress rule (SuperAdmin).
// { total, matches }.
export async function previewTagRuleMatches(rule) {
  return apiFetch('/api/tags/rules/preview', {
    method: 'POST',
    body: JSON.stringify({ rule }),
  });
}

// ── Stats — SQL API ────────────────────────────────────────────────────────────

export async function getStats(opts = {}) {
  const params = new URLSearchParams();
  if (opts.since) params.set('since', opts.since);
  const qs = params.toString();
  if (!opts.accessToken && !opts.since && _loginPrefetch?.stats) {
    const p = _loginPrefetch.stats;
    _loginPrefetch.stats = null;
    return p;
  }
  return apiFetch(`/api/stats${qs ? `?${qs}` : ''}`, { accessToken: opts.accessToken });
}

// ── Costs — SQL API ────────────────────────────────────────────────────────────
// Visible only to cost-tier roles (Supervisor / SupervisorView / SuperAdmin).
// UI must gate on user.canSeeCosts / user.canEditCosts before calling these.

export async function getCosts(caseId) {
  return apiFetch(`/api/cases/${caseId}/costs`);
}

export async function postCost(caseId, { amount, currency, categoryKey, incurredAt, notes }) {
  return apiFetch(`/api/cases/${caseId}/costs`, {
    method: 'POST',
    body: JSON.stringify({ amount, currency, categoryKey, incurredAt, notes }),
  });
}

export async function patchCost(costId, updates) {
  return apiFetch(`/api/costs/${costId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

export async function deleteCost(costId) {
  return apiFetch(`/api/costs/${costId}`, { method: 'DELETE' });
}

// Flat per-case cost aggregates — used by the dashboard for timeline-aware
// rollups (client-side aggregation against the visible/filtered case set).
export async function getCostsByCase() {
  return apiFetch('/api/costs/by-case');
}

// ── Comments — SQL API ────────────────────────────────────────────────────────

export async function getComments(caseId) {
  const data = await apiFetch(`/api/comments?caseId=${caseId}`);
  return (Array.isArray(data) ? data : []).map(c => ({
    id:          c.id,
    caseId:      c.caseId,
    text:        c.text,
    authorName:  c.authorName  || 'Unknown',
    authorEmail: c.authorEmail || null,
    // Role + response-clock side of the author at write time ('hq' | 'clinic' |
    // null = unknown). Acknowledge is offered only under 'hq' messages.
    authorRole:  c.authorRole  || null,
    authorSide:  c.authorSide  || null,
    isPublic:    c.isPublic    ?? true,
    created:     c.created     || null,
    attachments: (c.attachments || []).map(a => ({
      id:          a.id,
      name:        a.name,
      size:        a.size        || null,
      contentType: a.contentType || null,
      downloadUrl: a.downloadUrl || null,
      contentUrl:  a.downloadUrl || null,
      uploadedBy:  a.uploadedBy  || null,
      uploadedAt:  a.uploadedAt  || null,
      // Slot category + per-file upload metadata (mig 026) so comment pills can
      // show "taken dd/mm/yyyy" / "trainer X · trained dd/mm/yyyy".
      category:    a.category    || null,
      metadata:    parseAttachmentMetadata(a.metadata),
      serverRelativeUrl: a.downloadUrl || '',
    })),
    // "Acknowledge" likes — [{ userId, userName, createdAt, mine }] (mig 027).
    acks:        Array.isArray(c.acks) ? c.acks : [],
    // Snooze (mig 028): days this message snoozes the response clock once the
    // clinic acknowledges it; null for ordinary messages.
    snoozeDays:  c.snoozeDays == null ? null : Number(c.snoozeDays),
    _source:     c._source     || 'comment',
  }));
}

// ── Acknowledgements + response SLA (sla_escalation_logic.md) ─────────────────

/** Acknowledge a message → { commentId, acks }. 409 when already acknowledged. */
export async function ackComment(commentId) {
  return apiFetch(`/api/comments/${commentId}/ack`, { method: 'POST', body: JSON.stringify({}) });
}

/** The case's response-SLA state for the case-file chip. */
export async function getCaseSla(caseId) {
  return apiFetch(`/api/cases/${caseId}/sla`);
}

/** Supervisor control: action ∈ 'lift' | 'suppress' | 'resume', optional reason. */
export async function slaAction(caseId, action, reason = null) {
  return apiFetch(`/api/cases/${caseId}/sla/${encodeURIComponent(action)}`, {
    method: 'POST',
    body:   JSON.stringify(reason ? { reason } : {}),
  });
}

/**
 * postComment(caseId, text, isPublic, authorName, authorEmail, { snoozeDays })
 * `snoozeDays` (1–30) is the §4.1 "Snooze notifications" — sent only when set;
 * the API allows it for manageSla holders on public messages of SLA-eligible
 * cases (403 / 400 otherwise). Response carries `snoozeDays` (null when none).
 */
export async function postComment(caseId, text, isPublic = false, authorName = '', authorEmail = '', { snoozeDays = null } = {}) {
  // authorName / authorEmail accepted for API compat but the Functions layer uses
  // the auth token (mock user in dev). Will use real token values when JWT auth is wired.
  const body = { caseId, text, isPublic };
  if (snoozeDays != null) body.snoozeDays = snoozeDays;
  return apiFetch('/api/comments', {
    method: 'POST',
    body:   JSON.stringify(body),
  });
}

// ── Notifications ─────────────────────────────────────────────────────────────

export async function apiNotify(caseId, recipients, commentText, message, commentId) {
  if (!recipients?.length) return;
  return apiFetch('/api/notifications', {
    method: 'POST',
    body:   JSON.stringify({ type: 'comment_notify', caseId, recipients, commentText, message, commentId }),
  });
}

// "Request a document" notification — carries the requested document label only
// (no client/clinical data), sent alongside creating the requirement slot.
export async function apiRequirementRequest(caseId, recipients, requirementLabel, message, commentId) {
  if (!recipients?.length) return;
  return apiFetch('/api/notifications', {
    method: 'POST',
    body:   JSON.stringify({ type: 'requirement_request', caseId, recipients, requirementLabel, message, commentId }),
  });
}

// Dry-run ONE custom notification rule (v1 or v2, event or scheduled) against
// current cases — nothing is saved or sent. Body { rule } = the FULL rule
// object (not just its conditions). Gated like writing the notificationRules
// config key. Returns { total, scanned, capped, matches, cases[], recipients[],
// scheduled? } — see postNotificationRulePreview.js for the exact shapes.
export async function previewNotificationRule(rule) {
  return apiFetch('/api/notifications/rules/preview', {
    method: 'POST',
    body: JSON.stringify({ rule }),
  });
}

// Searchable Role list for the notification recipient picker: every job title
// held by enabled staff in Entra + every AE Tracker app role. Returns
// { jobTitles: [{ title, count }], appRoles: [{ value, label, assigned }], sources }.
export async function getNotificationRecipientOptions() {
  return apiFetch('/api/notifications/recipient-options');
}

// ── Case events — SQL API ─────────────────────────────────────────────────────

export async function getCaseEvents(caseId) {
  return apiFetch(`/api/cases/${caseId}/events`);
}

// ── Attachments — still SharePoint ───────────────────────────────────────────
// These stay SP-backed until Blob Storage is wired for attachment serving.

export async function fetchSpBlob(url) {
  const spToken = await getSpToken();
  if (!spToken) throw new Error('[client] SP token not available');
  const res = await fetch(url, { headers: { Authorization: `Bearer ${spToken}` } });
  if (!res.ok) throw new Error(`SP blob fetch ${res.status} ${res.statusText}`);
  return res.blob();
}

export async function getAttachmentBlob(caseId, fileName) {
  try {
    return await graphFetchBlob(
      `/sites/${SITE_ID}/lists/${AE_LIST_ID}/items/${caseId}/attachments/${encodeURIComponent(fileName)}/$value`
    );
  } catch { /* fall through */ }
  try {
    const drivePath = `Lists/Adverse%20Events/Attachments/${caseId}/${encodeURIComponent(fileName)}`;
    return await graphFetchBlob(`/sites/${SITE_ID}/drive/root:/${drivePath}:/content`);
  } catch { /* fall through */ }
  return null;
}


export async function deleteAttachment(attachmentId) {
  return apiFetch(`/api/attachments/${attachmentId}`, { method: 'DELETE' });
}

// Serialise per-category upload metadata for the `metadata` multipart field.
// Only a non-empty plain object is sent; the server allowlists keys per category
// (api/src/lib/uploadMetadata.js) and rejects metadata on other categories.
function metadataField(metadata) {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return null;
  const clean = Object.fromEntries(Object.entries(metadata).filter(([, v]) => v != null && v !== ''));
  return Object.keys(clean).length ? JSON.stringify(clean) : null;
}

export async function uploadCaseAttachment(caseId, file, { category, metadata } = {}) {
  const form = new FormData();
  form.append('file', file, file.name);
  if (category) form.append('category', category);
  const meta = metadataField(metadata);
  if (meta) form.append('metadata', meta);
  const res  = await fetch(`/api/cases/${caseId}/attachments`, {
    method: 'POST',
    headers: await apiAuthHeader(),
    body:    form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (data.code === SUB_SESSION_EXPIRED) announceSubSessionExpired();   // PIN pad over the page
    throw Object.assign(new Error(data.error || `Upload failed (${res.status})`), { status: res.status, code: data.code || null });
  }
  return data;
}

// ── Per-case requirements ────────────────────────────────────────────────────
// Admin-created asks shown in the "Required documents & sign-offs" section. Two
// kinds: a 'document' (satisfaction DERIVED from a 'req:<id>' attachment — no
// flag) and a 'signoff' (a task whose completion is STORED). Default 'document'.
export async function addCaseRequirement(caseId, { kind, key, label, note } = {}) {
  return apiFetch(`/api/cases/${caseId}/requirements`, {
    method: 'POST',
    body:   JSON.stringify({ kind, key, label, note }),
  });
}

export async function deleteCaseRequirement(caseId, reqId) {
  return apiFetch(`/api/cases/${caseId}/requirements/${encodeURIComponent(reqId)}`, {
    method: 'DELETE',
  });
}

// Complete a sign-off requirement. The optional message is recorded on the case
// (it is NOT emailed to the issuer — only case ref/clinic/label/who/when are).
export async function completeCaseRequirement(caseId, reqId, { message } = {}) {
  return apiFetch(`/api/cases/${caseId}/requirements/${encodeURIComponent(reqId)}/complete`, {
    method: 'POST',
    body:   JSON.stringify({ message }),
  });
}

// ── Requirement waivers ──────────────────────────────────────────────────────
// "No consent form — <reason>": records that a required document does not exist
// so the slot counts as satisfied. Gated server-side by canWaiveRequirements
// (capability `waiveReq`). Only consent_form is waivable in this release.
// Both return { waivers } — the case's full type_specific.waivers map.
export async function waiveRequirement(caseId, category, reason) {
  return apiFetch(`/api/cases/${caseId}/waivers`, {
    method: 'POST',
    body:   JSON.stringify({ category, reason }),
  });
}

export async function unwaiveRequirement(caseId, category) {
  return apiFetch(`/api/cases/${caseId}/waivers/${encodeURIComponent(category)}`, { method: 'DELETE' });
}

export async function uploadCommentAttachment(commentId, file, { category, metadata } = {}) {
  const form = new FormData();
  form.append('file', file, file.name);
  if (category) form.append('category', category);
  const meta = metadataField(metadata);
  if (meta) form.append('metadata', meta);

  const res = await fetch(`/api/comments/${commentId}/attachments`, {
    method:  'POST',
    headers: await apiAuthHeader(),  // no Content-Type — browser sets multipart boundary
    body:    form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (data.code === SUB_SESSION_EXPIRED) announceSubSessionExpired();   // PIN pad over the page
    throw Object.assign(new Error(data.error || `Upload failed (${res.status})`), { status: res.status, code: data.code || null });
  }
  return {
    id:                data.id,
    name:              data.fileName,
    sizeBytes:         data.sizeBytes,
    downloadUrl:       data.downloadUrl || null,
    serverRelativeUrl: data.downloadUrl || '',  // kept for backward compat with CommentItem render
  };
}

// ── Admin Config — SQL API ────────────────────────────────────────────────────
// Config lives in the SQL admin_config table (created via migration 003).

// `?versions=1` adds `__versions` ({ key: version }) for the stale-publish guard.
export async function getAdminConfig() {
  return apiFetch('/api/config?versions=1');
}

// `baseVersion` = the version the edit started from; the server refuses (409,
// err.data.stale) if the key was published since. Omit it to skip the check.
export async function saveAdminConfigKey(key, value, { publish = false, baseVersion } = {}) {
  return apiFetch(`/api/config/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body:   JSON.stringify({ value, publish, baseVersion }),
  });
}

// Atomic multi-key publish — every key lands in one DB transaction (each with
// its own audit row) or none does. Used when related keys (e.g. treatmentTypes +
// incidentCategoriesByTreatmentAndCode) must stay in sync.
export async function saveAdminConfigBatch(entries, { publish = false } = {}) {
  return apiFetch('/api/config', {
    method: 'PUT',
    body:   JSON.stringify({ entries, publish }),
  });
}

// Audit history for a governed config key (SuperAdmin only). Returns [] on any
// error so the UI degrades gracefully if the audit table isn't present yet.
export async function getConfigAudit(key, limit = 20) {
  try {
    const data = await apiFetch(`/api/config?audit=${encodeURIComponent(key)}&limit=${limit}`);
    return data.audit || [];
  } catch {
    return [];
  }
}

// Read-time integrity check of the audit hash chain (SuperAdmin only). Re-walks
// the whole chain server-side and returns { ok, count, brokenId?, reason? }.
export async function verifyConfigAudit() {
  return apiFetch('/api/config/verify-audit');
}

// ── PIN (session lock) ────────────────────────────────────────────────────────

export async function getPinStatus({ all = false } = {}) {
  return apiFetch(`/api/pin/status${all ? '?all=1' : ''}`);
}

// ── Access levels — Entra app-role assignments (SuperAdmin) ───────────────────
// Managed via Microsoft Graph, scoped to this app's roles. Read + add/change/remove.
export async function getAccessUsers() {
  return apiFetch('/api/access');                 // { roles: [{value,label}], users: [...] }
}
export async function resolveInheritedAccess(emails) {
  return apiFetch('/api/access/inherited', {
    method: 'POST',
    body: JSON.stringify({ emails: Array.isArray(emails) ? emails : [] }),
  });
}
// Live Microsoft 365 directory type-ahead (SuperAdmin) → { users: [{ id, name, email, jobTitle, department, office }] }
export async function searchDirectory(q) {
  return apiFetch(`/api/access/directory?q=${encodeURIComponent(q)}`);
}
export async function addAccessUser({ email, role }) {
  return apiFetch('/api/access', { method: 'POST', body: JSON.stringify({ email, role }) });
}
export async function changeAccessRole({ assignmentId, principalId, role }) {
  return apiFetch('/api/access', { method: 'PATCH', body: JSON.stringify({ assignmentId, principalId, role }) });
}
export async function removeAccessUser(assignmentId) {
  return apiFetch(`/api/access/${assignmentId}`, { method: 'DELETE' });
}
// Verified-travel mutes for the unexpected-country security alert (SuperAdmin).
export async function getAccessAlertMutes({ all = false } = {}) {
  return apiFetch(`/api/access-alert-mutes${all ? '?all=1' : ''}`);
}
export async function createAccessAlertMute({ email, country, days, reason }) {
  return apiFetch('/api/access-alert-mutes', {
    method: 'POST',
    body: JSON.stringify({ email, country: country || null, days, reason: reason || null }),
  });
}
export async function revokeAccessAlertMute(id) {
  return apiFetch(`/api/access-alert-mutes/${id}`, { method: 'DELETE' });
}

export async function createEntraAppRole({ value, displayName, description }) {
  return apiFetch('/api/access/approles', {
    method: 'POST',
    body: JSON.stringify({ value, displayName, description }),
  });
}

export async function setPin(pin, currentPin) {
  return apiFetch('/api/pin/set', {
    method: 'POST',
    body: JSON.stringify(currentPin ? { pin, currentPin } : { pin }),
  });
}

export async function verifyPin(pin) {
  return apiFetch('/api/pin/verify', {
    method: 'POST',
    body: JSON.stringify({ pin }),
  });
}

// Per-user auto-lock duration (Supervisor + SuperAdmin). ms > 0 = window,
// 0 = "Never" (SuperAdmin only), null = clear → role default.
export async function setLockPref(lockInactivityMs) {
  return apiFetch('/api/pin/lock-pref', {
    method: 'POST',
    body: JSON.stringify({ lockInactivityMs }),
  });
}

export async function adminResetPin(email) {
  return apiFetch(`/api/pin/${encodeURIComponent(email)}`, { method: 'DELETE' });
}

export async function recordAccessSession() {
  return apiFetch('/api/access/log', {
    method: 'POST',
    body: JSON.stringify({ event: 'session_start' }),
  });
}

/** SuperAdmin — sign-in / PIN activity log. */
export async function getAccessLogs({
  email,
  events,
  exclude,
  from,
  to,
  limit = 15,
  offset = 0,
  sort = null,
  dir = 'desc',
} = {}) {
  const params = new URLSearchParams();
  if (email) params.set('email', email);
  if (events?.length) params.set('events', events.join(','));
  if (exclude?.length) params.set('exclude', exclude.join(','));
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  if (limit) params.set('limit', String(limit));
  if (offset) params.set('offset', String(offset));
  // Sorting is server-side — the log is paginated, so sorting what is on screen
  // would reorder the page instead of the log.
  if (sort) { params.set('sort', sort); params.set('dir', dir === 'asc' ? 'asc' : 'desc'); }
  const qs = params.toString();
  return apiFetch(`/api/access/logs${qs ? `?${qs}` : ''}`);
}

export async function requestPinReset({ note = '', clinic = '' } = {}) {
  return apiFetch('/api/pin/forgot-request', {
    method: 'POST',
    body: JSON.stringify({ note, clinic }),
  });
}

// ── Directory: department staff (Medical Team Member picker + notify routing) ──
// Returns { department, staff: [{ name, jobTitle, email }], source }. Email is
// included so the selected person can be notified. Fails soft to an empty list.
export async function getReferenceStaff(department) {
  const qs = department ? `?department=${encodeURIComponent(department)}` : '';
  return apiFetch(`/api/reference/staff${qs}`);
}

